import { RenderStoryOptions, SmartPillArbitrator, SmartPillData } from 'owa-smart-pill';
import isPillTypeEnabled from 'owa-mail-smart-pill-features/lib/utils/isPillTypeEnabled';
import isConfidenceScoreAcceptable from 'owa-mail-smart-pill-features/lib/utils/isConfidenceScoreAcceptable';
import getSmartPillsRenderingStrategy from '../strategies/getSmartPillsRenderingStrategy';
import SmartPillsRenderingStrategy from '../strategies/SmartPillsRenderingStrategy';

const ARBITRATOR_ID: string = 'complexArbitrator';

/**
 * Arbitrator that can handle smart doc, smart time, and smart reply pills
 * Priority is as follows:
 * 1. Smart doc (not mixed with other pill types)
 * 2. Smart time (by itself or mixed with smart reply)
 * 3. Smart reply
 */
export const complexArbitrator: SmartPillArbitrator = {
    arbitratePills: (pills: SmartPillData[]) => {
        const pillsToShow: SmartPillData[] = [];
        const pillsToHide: SmartPillData[] = [];

        if (pills) {
            // First: Filter pills by feature.
            const pillsFilteredByFeature: SmartPillData[] = filterPills(
                pills,
                pillsToHide,
                isPillTypeEnabled,
                RenderStoryOptions.FeatureIsNotEnabled
            );

            // Second: Filter pills by confidence score.
            const pillsFullyFiltered: SmartPillData[] = filterPills(
                pillsFilteredByFeature,
                pillsToHide,
                isConfidenceScoreAcceptable,
                RenderStoryOptions.ConfidenceLevelTooLow
            );

            // Now pick which pills to show.
            if (pillsFullyFiltered.length > 0) {
                // Get a strategy
                const renderingStrategy: SmartPillsRenderingStrategy = getSmartPillsRenderingStrategy();

                // End filtering by selecting pills based on strategy
                pillsToShow.push(...renderingStrategy.selectPills(pillsFullyFiltered));

                // Populate pillsToHide with any pills not included in pillsToShow.
                for (let i = 0; i < pillsFullyFiltered.length; i++) {
                    const filteredPill: SmartPillData = pillsFullyFiltered[i];
                    if (!pillsToShow.some(pillToShow => pillToShow === filteredPill)) {
                        filteredPill.logData.mainPayload.renderStory |=
                            RenderStoryOptions.RejectedByArbitrator;
                        pillsToHide.push(filteredPill);
                    }
                }
            }
        }

        return {
            pillsToShow: pillsToShow,
            pillsToHide: pillsToHide,
            arbitratorId: ARBITRATOR_ID,
        };
    },
};

/**
 * @param pills: Array of pills to filter on
 * @param pillsToHide: Array to place filtered-out pills into
 * @param filterFunction: Function used to filter pills
 * @param renderStoryOption: RenderStoryOption for filtered-out pills
 */
function filterPills(
    pills: SmartPillData[],
    pillsToHide: SmartPillData[],
    filterFunction: (pill: SmartPillData) => boolean,
    renderStoryOption: RenderStoryOptions
): SmartPillData[] {
    const filteredPills: SmartPillData[] = [];
    pills.forEach(pill => {
        if (filterFunction(pill)) {
            filteredPills.push(pill);
        } else {
            pill.logData.mainPayload.renderStory |= renderStoryOption;
            pillsToHide.push(pill);
        }
    });

    return filteredPills;
}

export default complexArbitrator;
